import * as React from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  GridRowModesModel,
  GridColDef,
  GridColumnHeaderParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid';
import {
  GridActionsCellItem,
  GridRowId,
  GridRowModes,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { ChargeTypeInstance } from '../../../data/models/pricing-model.model';
import * as lookupSlice from '../../../data/lookup.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { MultiSelectEditCell } from '../../forms/MultiSelectEditCell';
import { Box, Checkbox, styled } from '@mui/material';
import { DataGridPro } from '../../tables/DataGridPro';
import { useEffect, useState } from 'react';
import { SelectEditCell } from '../../forms/SelectEditCell';
import {
  GroupedSelectCellMenuGroup,
  GroupedSelectEditCell,
} from '../../forms/GroupedSelectEditCell';
import lowerFirst from 'lodash/lowerFirst';

const PREFIX = 'ChargeTypeTable';

const classes = {
  selectCell: `${PREFIX}-selectCell`,
  readOnlyRow: `${PREFIX}-readOnlyRow`,
  lastRow: `${PREFIX}-lastRow`,
};

const StyledDataGridPro = styled(DataGridPro)(({ theme }) => ({
  '& .MuiDataGrid-cell': {
    borderColor: '#0000001f',
  },
  '& .MuiDataGrid-cell:not(:nth-last-child(-n+2)):not(.MuiDataGrid-cell--editing)':
    {
      paddingLeft: `11px !important`,
    },
  '& .MuiDataGrid-cell--editing:focus-within': {
    outline: 'none !important',
  },
  '& .MuiDataGrid-row--editing': {
    backgroundColor: `white !important`,
  },
  '& .MuiDataGrid-cell--editing': {
    backgroundColor: `${theme.palette.lightActionDisabled04p.main} !important`,
  },
  [`& .${classes.selectCell}`]: {
    padding: `${theme.spacing(1)} !important`,

    '& .MuiInputBase-root': {
      height: 38,
      backgroundColor: 'white',
    },
    '& .MuiSelect-select': {
      paddingTop: 5,
    },
  },
  [`& .${classes.readOnlyRow}`]: {
    backgroundColor: '#0000000f',
    pointerEvents: 'none',
  },
  [`& .${classes.lastRow}`]: {
    '.MuiDataGrid-cell': {
      borderBottom: `1px solid #0000001f !important`,
    },
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    width: 24,
    height: 24,
  },

  '&.Mui-disabled': {
    color: '#00000099',
  },
  '&.Mui-disabled.Mui-checked': {
    color: '#00000099',
  },
}));

const StyledAsterisk = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
}));

const StyledDeleteIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  height: 24,
  width: 24,
  marginBottom: 1,
}));

const StyledGridActionsBox = styled(Box)(({ theme }) => ({
  backgroundColor: `${theme.palette.lightActionDisabled04p.main} !important`,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
}));

interface ChargeTypeTableProps {
  readonly: boolean;
  chargeTypeInstances: ChargeTypeInstance[];
  setValid?: (valid: boolean) => void;
  setRows: React.Dispatch<React.SetStateAction<ChargeTypeInstance[]>>;
}

enum ChargeTypeGroup {
  Matrix = 'Matrix',
  AdminFeePlusCost = 'AdminFeePlusCost',
  PMPM = 'pmpm',
  Other = 'Other',
}

export default function ChargeTypeTable({
  readonly,
  chargeTypeInstances,
  setValid,
  setRows,
}: ChargeTypeTableProps) {
  const { t } = useTranslation('common');
  const chargeTypes = useAppSelector(lookupSlice.selectChargeTypes);
  const pricingFunctions = useAppSelector(
    accountingSlice.selectPricingFunctions,
  );
  const calculationGroups = useAppSelector(
    lookupSlice.selectPricingCalculationGroups,
  );
  const [hoveredRow, setHoveredRow] = useState<string | undefined>(undefined);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  // Sorting done client-side, as requested by the business
  // Charge types not in a grouping will appear under "Other"
  const chargeTypeGroupings: Record<ChargeTypeGroup, string[]> = {
    [ChargeTypeGroup.Matrix]: [
      'PICKUP',
      'MILEAGE',
      'WAIT',
      'NOLOAD',
      'LATECANCEL',
    ],
    [ChargeTypeGroup.AdminFeePlusCost]: ['TOTALCHARGE', 'ADMIN'],
    [ChargeTypeGroup.PMPM]: ['MONTHLY', 'UTILTIER'],
    [ChargeTypeGroup.Other]: [],
  };

  // Get all known charge type codes
  const knownChargeTypeBCs = new Set(Object.values(chargeTypeGroupings).flat());

  // Add unknown charge types to "Other" group
  chargeTypes.forEach((chargeType) => {
    if (!knownChargeTypeBCs.has(chargeType.chargeTypeBC)) {
      chargeTypeGroupings[ChargeTypeGroup.Other].push(chargeType.chargeTypeBC);
    }
  });

  const chargeTypeOptions: GroupedSelectCellMenuGroup[] = Object.entries(
    chargeTypeGroupings,
  ).map(([groupName, groupValues]) => ({
    groupName: t(`pricingmodel.${lowerFirst(groupName)}`),
    items: chargeTypes
      .filter((chargeType) => groupValues.includes(chargeType.chargeTypeBC))
      .map((chargeType) => ({
        label: chargeType.chargeTypeName,
        value: `${groupName}#${chargeType.chargeTypeBC}`,
      })),
  }));

  useEffect(() => {
    if (chargeTypeInstances.length === 0) {
      setRowModesModel({});
    }
    chargeTypeInstances.forEach((row) => {
      if (row.isNew && row.isUnsaved) {
        setRowModesModel({
          ...rowModesModel,
          [row.temporaryUUID!]: { mode: GridRowModes.Edit },
        });
      }
    });
  }, [chargeTypeInstances]);

  useEffect(() => {
    if (!setValid) {
      return;
    }
    let isValid = true;

    // Make sure there is at least one component
    if (!chargeTypeInstances.length) {
      isValid = false;
    }

    // Make sure no rows are in edit mode.
    const keys = Object.keys(rowModesModel);
    keys.forEach((key) => {
      if (rowModesModel[key].mode === GridRowModes.Edit) {
        isValid = false;
      }
    });

    setValid(isValid);
  }, [chargeTypeInstances, rowModesModel, chargeTypeInstances]);

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(chargeTypeInstances.filter((row) => row.temporaryUUID !== id));
    const updatedRowModesModel = { ...rowModesModel };
    delete updatedRowModesModel[id];
    setRowModesModel(updatedRowModesModel);
  };

  const processRowUpdate = (
    newRow: GridValidRowModel,
    _oldRow: GridValidRowModel,
  ) => {
    const updatedRow: ChargeTypeInstance = {
      ...(newRow as ChargeTypeInstance),
    };

    if (
      !newRow.chargeTypeBC ||
      !newRow.pricingFunctionBC ||
      !newRow.calculationGroupBC
    ) {
      throw new Error(t('pricingmodel.requiredFields'));
    }

    const newRows = chargeTypeInstances.map((row) =>
      row.temporaryUUID === newRow.temporaryUUID ? updatedRow : row,
    );
    setRows(newRows);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const getChargeTypeName = (chargeTypeValue: string) => {
    const chargeTypeOption = chargeTypeOptions
      .map((options) => options.items)
      .flat()
      .find((item) => item.value === chargeTypeValue);
    return chargeTypeOption?.label;
  };

  const getPricingFunctionName = (pricingFunctionBC: string) => {
    const pricingFunction = pricingFunctions.find(
      (pf) => pf.pricingFunctionBC === pricingFunctionBC,
    );
    return pricingFunction ? pricingFunction.name : '';
  };

  const getCalculationGroupName = (groupBC: string) => {
    const group = calculationGroups.find(
      (cg) => cg.pricingCalculationGroupBC === groupBC,
    );
    return group ? group.pricingCalculationGroupName : '';
  };

  const formatCalculationGroups = (values: string[]) => {
    return values
      ? values.map((v) => getCalculationGroupName(v)).join(', ')
      : '';
  };

  const renderHeaderWithoutAsterisk = (params: GridColumnHeaderParams) => {
    const raw = params.colDef.headerName || '';
    let headerText: string | JSX.Element = readonly
      ? raw.replace(/\s*\*$/, '')
      : raw;
    if (headerText.endsWith(' *')) {
      const textWithoutAsterisk = headerText.slice(0, -2);
      headerText = (
        <>
          {textWithoutAsterisk}
          <StyledAsterisk> *</StyledAsterisk>
        </>
      );
    }
    return <div className="MuiDataGrid-columnHeaderTitle">{headerText}</div>;
  };

  const columns: GridColDef[] = [
    {
      field: 'chargeTypeBC',
      headerName: t('pricingmodel.pricingComponentChargeType'),
      width: 152,
      editable: true,
      sortable: false,
      valueOptions: chargeTypeOptions,
      cellClassName: (params) => classes.selectCell,
      getOptionLabel: (value: any) => {
        return getChargeTypeName(value) ?? '';
      },
      valueGetter: (params) => {
        const { value } = params;
        if (!value || value.includes('#')) {
          return value;
        }

        const grouping = Object.entries(chargeTypeGroupings).find(
          ([, groupValues]) => groupValues.includes(value),
        );

        return grouping ? `${grouping[0]}#${value}` : value;
      },
      valueSetter: (params) => {
        const { row, value } = params;
        if (value && value.includes('#')) {
          return { ...row, chargeTypeBC: value.split('#')[1] };
        }
        return {
          ...row,
          chargeTypeBC: value,
        };
      },
      valueFormatter: ({ value }) => {
        if (value) {
          return getChargeTypeName(value);
        }
        return value;
      },
      renderHeader: renderHeaderWithoutAsterisk,
      renderEditCell: GroupedSelectEditCell,
    },
    {
      field: 'pricingFunctionBC',
      headerName: t('pricingmodel.pricingFunction'),
      width: 198,
      editable: true,
      sortable: false,
      type: 'singleSelect',
      valueOptions: pricingFunctions,
      cellClassName: (params) => classes.selectCell,
      getOptionValue: (value: any) => {
        return {
          value: value.pricingFunctionBC,
          tooltip: value.description,
        };
      },
      valueFormatter: ({ value }) => {
        return pricingFunctions.find((func) => func.pricingFunctionBC === value)
          ?.name;
      },
      getOptionLabel: (value: any) =>
        getPricingFunctionName(value.pricingFunctionBC),
      renderEditCell: SelectEditCell,
      renderHeader: renderHeaderWithoutAsterisk,
    },
    {
      field: 'calculationGroupBC',
      headerName: t('pricingmodel.pricingComponentGroup'),
      width: 162,
      editable: true,
      sortable: false,
      type: 'singleSelect',
      valueOptions: calculationGroups,
      cellClassName: (params) => classes.selectCell,
      getOptionValue: (value: any) => ({
        value: value.pricingCalculationGroupBC,
      }),
      valueFormatter: ({ value }) => {
        return calculationGroups.find(
          (calcGroup) => calcGroup.pricingCalculationGroupBC === value,
        )?.pricingCalculationGroupName;
      },
      getOptionLabel: (value: any) =>
        getCalculationGroupName(value.pricingCalculationGroupBC),
      renderHeader: renderHeaderWithoutAsterisk,
      renderEditCell: SelectEditCell,
    },
    {
      field: 'calculationOverrideGroupBCs',
      headerName: t('pricingmodel.pricingComponentOverride'),
      width: 162,
      editable: true,
      sortable: false,
      type: 'singleSelect',
      valueOptions: calculationGroups,
      cellClassName: (params) => classes.selectCell,
      getOptionValue: (value: any) => value.pricingCalculationGroupBC,
      getOptionLabel: (value: any) =>
        getCalculationGroupName(value.pricingCalculationGroupBC),
      valueFormatter: ({ value }) => formatCalculationGroups(value),
      renderEditCell: (params) =>
        MultiSelectEditCell({ ...params, placeholder: t('common.select') }),
      filterOperators: [
        {
          value: 'contains',
          getApplyFilterFn: (filterItem) => {
            if (filterItem.value == null || filterItem.value === '') {
              return null;
            }
            return ({ value }) => {
              return value.some(
                (cellValue: any) => cellValue === filterItem.value,
              );
            };
          },
        },
      ],
    },
    {
      field: 'isRequired',
      headerName: t('pricingmodel.pricingComponentRequired'),
      width: 112,
      editable: true,
      sortable: false,
      type: 'boolean',
      renderCell: (params) => (
        <StyledCheckbox checked={params.row.isRequired} disabled={readonly} />
      ),
      renderEditCell: (params) => (
        <StyledCheckbox
          checked={params.row.isRequired}
          disabled={readonly}
          onChange={(e) => {
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.checked,
            });
          }}
        />
      ),
    },
    {
      field: 'action',
      headerName: '',
      sortable: false,
      width: 50,
      renderCell: (params) => {
        const isEditMode = rowModesModel[params.id]?.mode === 'edit';
        if (!readonly && isEditMode) {
          if (params.row.temporaryUUID === hoveredRow) {
            return (
              <StyledGridActionsBox>
                <GridActionsCellItem
                  icon={<StyledDeleteIcon />}
                  label="Delete"
                  onClick={handleDeleteClick(params.row.temporaryUUID)}
                  color="inherit"
                />
              </StyledGridActionsBox>
            );
          } else {
            return <StyledGridActionsBox />;
          }
        }
      },
    },
  ];

  const getRowClassName = (params: GridRowClassNameParams) => {
    let className = readonly ? classes.readOnlyRow : '';
    const isLastRow =
      params.indexRelativeToCurrentPage === chargeTypeInstances.length - 1;
    if (isLastRow) {
      className = `${className} ${classes.lastRow}`;
    }
    return className;
  };

  return (
    <StyledDataGridPro
      getRowId={(row) => row.temporaryUUID}
      rowHeight={60}
      rows={chargeTypeInstances}
      columns={columns}
      editMode="row"
      autoHeight={true}
      hideFooter={true}
      disableColumnMenu={true}
      rowModesModel={rowModesModel}
      onRowModesModelChange={handleRowModesModelChange}
      processRowUpdate={processRowUpdate}
      isCellEditable={() => !readonly}
      disableRowSelectionOnClick={readonly}
      getRowClassName={getRowClassName}
      slotProps={{
        row: {
          onMouseEnter: (event) => {
            const id = event.currentTarget.dataset.id;
            setHoveredRow(id);
            if (id) {
              setRowModesModel((prev) => ({
                ...prev,
                [id]: { mode: GridRowModes.Edit },
              }));
            }
          },
          onMouseLeave: (event) => {
            setHoveredRow(undefined);
            const id = event.currentTarget.dataset.id;
            if (id) {
              setRowModesModel((prev) => ({
                ...prev,
                [id]: { mode: GridRowModes.View },
              }));
            }
          },
        },
      }}
    />
  );
}
